import { ProjectWithAssigneesDto } from '../../../commonTypes/acs';
import { AssigneeDto, ProjectAssigneesWithUserInfoDto, EmploymentType } from '../../../commonTypes/project.types';
import { useSelector, useDispatch } from 'react-redux';
import { selectProjectAssignees, setProjectAssignees } from '../../../store/slices/project-assignees.slice';
import { selectOrganizationMembers } from '../../../store/slices/add-edit-policy.slice';
import { selectAuth } from '../../../store/slices/auth.slice';
import { RootState } from '../../../store';
import { useEffect, useState, useMemo } from 'react';
import { IconButton } from '../../Common';
import AssignMembersDrawer from '../../AssignMembersDrawer';
import {
    useHttpGetProjectRoles,
    useHttpPostProjectAssignees,
    useHttpGetMembersOMX,
    useOrganizationId,
    useHttpGetProjectTotalEarnings,
} from '../../../hooks';
import useHttpPatchProjectAssignee from '../../../hooks/project/projectApi/useHttpPatchProjectAssignee';
import { useHttpGetProjectHoursSummary } from '../../../hooks/timelog/timelogApi';
import { RateType } from '@cyberlabrs/helpers';
import { RoleTypeEnum } from '../../../commonTypes/user.types';

interface ProjectOverviewTabProps {
    project: ProjectWithAssigneesDto;
    onEditMember?: (member: ProjectAssigneesWithUserInfoDto) => void;
    onRefresh?: () => void;
    canEdit?: boolean;
    showRates?: boolean;
    showMetrics?: boolean;
}

interface MetricCardProps {
    icon: string;
    title: string;
    value: string;
    change?: number;
}

const MetricCard = ({ icon, title, value, change }: MetricCardProps) => {
    const isPositive = change !== undefined && change > 0;
    const isNegative = change !== undefined && change < 0;

    return (
        <div className="metric-card">
            <div className="metric-card__header">
                <img src={icon} alt={title} className="metric-card__icon" />
                <span className="metric-card__title">{title}</span>
            </div>
            <div className="metric-card__content">
                <h3>{value}</h3>
                {change !== undefined && (
                    <span
                        className={`metric-card__change ${isPositive ? 'metric-card__change--positive' : ''} ${
                            isNegative ? 'metric-card__change--negative' : ''
                        }`}
                    >
                        {Math.abs(change)}% {isPositive ? '↗' : '↘'}
                    </span>
                )}
            </div>
        </div>
    );
};

const MemberCard = ({ member, showRates, canEdit, onEdit }: any) => {
    const fullName = `${member.givenName || ''} ${member.familyName || ''}`.trim();
    const jobTitle = member.title || 'No title set';

    const fmtRate = (rate?: number) => (rate == null ? 'Not set' : `$ ${rate.toFixed(2)}`);
    const fmtType = (x?: string) => (!x ? 'Not set' : x.charAt(0).toUpperCase() + x.slice(1).toLowerCase());

    return (
        <div className="member-card">
            <div className="member-card__header">
                <img
                    className="member-card__avatar"
                    src={member.imageUrl || '/svg/person.svg'}
                    alt={fullName}
                    onError={e => ((e.target as HTMLImageElement).src = '/svg/person.svg')}
                />
                <div className="member-card__info">
                    <h4 className="member-card__name">{fullName}</h4>
                    <p className="member-card__role">{jobTitle}</p>
                </div>
                {canEdit && (
                    <IconButton
                        onClick={() => onEdit?.(member)}
                        iconUrl="/svg/pen-grey.svg"
                        iconAlt="edit"
                        variant="icon"
                        className="member-card__edit"
                    />
                )}
            </div>

            {showRates && (
                <div className="member-card__details">
                    <p className="member-card__label">Hourly rate</p>
                    <span className="member-card__separator">/</span>
                    <p className="member-card__value">{fmtRate(member.rate)}</p>

                    <p className="member-card__label">Type</p>
                    <span className="member-card__separator">/</span>
                    <p className="member-card__value">{fmtType(member.rateType)}</p>
                </div>
            )}
        </div>
    );
};

export const ProjectOverviewTab = ({ project, onRefresh, canEdit = true, showRates = true, showMetrics }: ProjectOverviewTabProps) => {
    const dispatch = useDispatch();
    const [isAssignMemberDrawerOpen, setIsAssignMemberDrawerOpen] = useState(false);
    const [editingMember, setEditingMember] = useState<ProjectAssigneesWithUserInfoDto | null>(null);
    const orgId = useOrganizationId();

    const projectAssignees = useSelector((s: RootState) => selectProjectAssignees(s, project.id));
    const orgMembers = useSelector(selectOrganizationMembers);
    const authUser = useSelector(selectAuth);

    const canViewMetrics =
        showMetrics !== undefined
            ? showMetrics
            : ![RoleTypeEnum.ORDINARY, RoleTypeEnum.BASIC, RoleTypeEnum.TEAM_LEAD, RoleTypeEnum.PM].includes(authUser.role.name);

    const { roles } = useHttpGetProjectRoles(canEdit);
    const { exec: fetchMembers } = useHttpGetMembersOMX(orgId);
    const { exec: execAddAssignees } = useHttpPostProjectAssignees();
    const { exec: execPatchAssignee } = useHttpPatchProjectAssignee();
    const { data: hoursSummary } = useHttpGetProjectHoursSummary(project.id, canViewMetrics);
    const { data: earningsData } = useHttpGetProjectTotalEarnings(project.id, canViewMetrics);

    useEffect(() => fetchMembers(), []);

    useEffect(() => {
        if (project.projectAssignees?.length) {
            dispatch(setProjectAssignees({ projectId: project.id, assignees: project.projectAssignees }));
        }
    }, [project.projectAssignees, project.id, dispatch]);

    const assignees = projectAssignees.length ? projectAssignees : project.projectAssignees || [];

    const enrichedAssignees = useMemo(() => {
        return assignees.map(assignee => {
            if (assignee.givenName || assignee.familyName || assignee.imageUrl || assignee.title) {
                return assignee;
            }

            const orgMember = orgMembers.find(m => m.userSub === assignee.userSub);
            if (orgMember) {
                return {
                    ...assignee,
                    givenName: orgMember.givenName,
                    familyName: orgMember.familyName,
                    imageUrl: orgMember.imageUrl,
                    title: orgMember.title,
                };
            }

            return assignee;
        });
    }, [assignees, orgMembers]);

    const activeMembers = enrichedAssignees.filter(a => a.active);

    const availableMembers = useMemo(() => {
        return orgMembers.map(m => ({
            label: `${m.givenName} ${m.familyName}`,
            value: m.userSub,
            imageUrl: m.imageUrl,
            jobTitle: m.title,
        }));
    }, [orgMembers]);

    const assignedUserSubs = useMemo(() => new Set(assignees.map(a => a.userSub)), [assignees]);

    const unassignedMembers = useMemo(() => {
        return availableMembers.filter(m => !assignedUserSubs.has(m.value));
    }, [availableMembers, assignedUserSubs]);

    const employmentTypes = [
        { label: 'Full Time', value: 'Full-time' },
        { label: 'Part Time', value: 'Part-time' },
        { label: 'Contract', value: 'Contract' },
    ];

    const handleEditMember = (member: ProjectAssigneesWithUserInfoDto) => {
        setEditingMember(member);
        setIsAssignMemberDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setIsAssignMemberDrawerOpen(false);
        setEditingMember(null);
    };

    const handleAssignMembers = (members: any[]) => {
        // If editing, use the patch endpoint
        if (editingMember && members.length === 1) {
            const m = members[0];
            const bill = m.billableType === 'billable';

            execPatchAssignee(
                project.id,
                editingMember.userSub,
                {
                    billable: bill,
                    rate: bill ? parseFloat(m.rate) || 0 : 0,
                    rateType: bill ? RateType.HOURLY : RateType.HOURLY,
                    employmentType: m.employmentType as EmploymentType,
                },
                {
                    onSuccess: () => {
                        handleCloseDrawer();
                        onRefresh?.();
                    },
                }
            );
        } else {
            // Otherwise, add new assignees
            const payload: AssigneeDto[] = members.map(m => {
                const bill = m.billableType === 'billable';
                return {
                    userSub: m.memberId,
                    billable: bill,
                    rate: bill ? parseFloat(m.rate) || 0 : undefined,
                    rateType: bill ? RateType.HOURLY : undefined,
                    employmentType: bill ? m.employmentType : undefined,
                    assignedRoles: [{ projectRoleId: Number(m.role) }],
                };
            });

            execAddAssignees(
                project.id,
                { assignees: payload },
                {
                    onSuccess: () => {
                        handleCloseDrawer();
                        onRefresh?.();
                    },
                }
            );
        }
    };

    return (
        <div className="project-overview">
            {canViewMetrics && (
                <div className="project-overview-tab__metrics">
                    <MetricCard
                        icon="/svg/dollar.svg"
                        title="Total Earnings"
                        value={`$ ${
                            earningsData?.totalEarnings?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'
                        }`}
                        change={earningsData?.percentageDifference}
                    />
                    <MetricCard
                        icon="/svg/clock.svg"
                        title="Total Logged Hours"
                        value={`${hoursSummary?.totalSubmittedHours?.toLocaleString('en-US') || '0'} Hours`}
                        change={
                            hoursSummary
                                ? hoursSummary.changeDirection === 'down'
                                    ? -hoursSummary.percentageChange
                                    : hoursSummary.changeDirection === 'up'
                                    ? hoursSummary.percentageChange
                                    : undefined
                                : undefined
                        }
                    />
                    <MetricCard
                        icon="/svg/member.svg"
                        title="Total Members"
                        value={`${activeMembers.length} Member${activeMembers.length !== 1 ? 's' : ''}`}
                    />
                </div>
            )}

            <div className="project-overview-tab">
                <div className="project-overview-tab__section">
                    <h2 className="project-overview-tab__title">{project.name}</h2>
                    {project.description && <p className="project-overview-tab__description">{project.description}</p>}
                </div>

                <div className="project-overview-tab__section">
                    <div className="project-overview-tab__section-header">
                        <h3 className="project-overview-tab__section-title">Members</h3>
                    </div>

                    <div className="project-overview-tab__members">
                        {activeMembers.length ? (
                            <>
                                {activeMembers.map(m => (
                                    <MemberCard key={m.userSub} member={m} canEdit={canEdit} showRates={showRates} onEdit={handleEditMember} />
                                ))}

                                {canEdit && (
                                    <div className="member-card__add-member" onClick={() => setIsAssignMemberDrawerOpen(true)}>
                                        <div className="member-card__add-member-circle">
                                            <img src="/svg/plus.svg" />
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="member-card__add-member" onClick={() => setIsAssignMemberDrawerOpen(true)}>
                                <div className="member-card__add-member-circle">
                                    <img src="/svg/plus.svg" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <AssignMembersDrawer
                open={isAssignMemberDrawerOpen}
                onClose={handleCloseDrawer}
                onAssign={handleAssignMembers}
                availableMembers={unassignedMembers}
                employmentTypes={employmentTypes}
                projectRoles={roles}
                editMode={!!editingMember}
                editMember={editingMember || undefined}
            />
        </div>
    );
};
